import { useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { Download, Printer } from 'lucide-react';
import { URL_ETIQUETA } from './comum';

// Etiqueta do equipamento, da via/setor e do parque (0029), desenhada como SVG em milímetros: o mesmo desenho vai
// para a tela, para a impressão do navegador e para o arquivo (SVG vetorial / PNG 600 dpi) da impressora UV.
// QR com correção Q (~25 %): aguenta sujeira e desgaste de etiqueta na rua. Sem IP/MAC: só identificação.

export type TipoEtiqueta = 'parque' | 'setor' | 'via' | 'equipamento';
const NOME_TIPO: Record<TipoEtiqueta, string> = { parque: 'PARQUE', setor: 'SETOR', via: 'VIA DE ACESSO', equipamento: 'EQUIPAMENTO' };
const TAMANHOS = { pequena: { w: 50, h: 30, nome: '50 × 30 mm' }, media: { w: 80, h: 50, nome: '80 × 50 mm' }, grande: { w: 100, h: 60, nome: '100 × 60 mm' } } as const;
type Tamanho = keyof typeof TAMANHOS;
const VERMELHO = '#87191B';   // identidade MAZA (material que fica no cliente)

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
/** Quebra em linhas pela largura aproximada da fonte (Arial ≈ 0,52 × altura por caractere). */
function quebrar(texto: string, larguraMm: number, fonteMm: number, maxLinhas: number): string[] {
  const cabe = Math.max(4, Math.floor(larguraMm / (fonteMm * 0.52)));
  const linhas: string[] = [];
  let atual = '';
  for (const p of texto.split(/\s+/).filter(Boolean)) {
    const tentativa = atual ? `${atual} ${p}` : p;
    if (tentativa.length <= cabe) { atual = tentativa; continue; }
    if (atual) linhas.push(atual);
    atual = p.length > cabe ? `${p.slice(0, cabe - 1)}…` : p;
    if (linhas.length === maxLinhas) break;
  }
  if (atual && linhas.length < maxLinhas) linhas.push(atual);
  if (linhas.length === maxLinhas && texto.length > linhas.join(' ').length + 1) linhas[maxLinhas - 1] = `${linhas[maxLinhas - 1]!.replace(/…$/, '').slice(0, cabe - 1)}…`;
  return linhas;
}

export function svgEtiqueta({ codigo, tipo, titulo, sub, tamanho }: { codigo: string; tipo: TipoEtiqueta; titulo: string; sub: string; tamanho: Tamanho }): string {
  const { w: W, h: H } = TAMANHOS[tamanho];
  const m = H * 0.07, q = H - 2 * m, k = H / 30;   // k = escala em relação à etiqueta de 30 mm
  const qr = QRCode.create(URL_ETIQUETA(codigo), { errorCorrectionLevel: 'Q' });
  const n = qr.modules.size, cel = q / n;
  let mods = '';
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.modules.get(r, c)) mods += `M${(m + c * cel).toFixed(3)} ${(m + r * cel).toFixed(3)}h${cel.toFixed(3)}v${cel.toFixed(3)}h-${cel.toFixed(3)}z`;
  const x = m + q + m * 0.9, larg = W - x - m;
  const faixa = 3.6 * k;
  const fT = 2.9 * k, fS = 2.1 * k, fC = 3.6 * k, fR = 1.6 * k;
  const linT = quebrar(titulo, larg, fT, 2), linS = quebrar(sub, larg, fS, 2);
  let y = m + faixa + fT * 1.15;
  const txt = (l: string, f: number, peso: number, cor = '#000') => { const s = `<text x="${x.toFixed(2)}" y="${y.toFixed(2)}" font-size="${f.toFixed(2)}" font-weight="${peso}" fill="${cor}">${esc(l)}</text>`; y += f * 1.18; return s; };
  const corpo = linT.map((l) => txt(l, fT, 700)).join('') + (y += fS * 0.25, '') + linS.map((l) => txt(l, fS, 400, '#222')).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}mm" height="${H}mm" viewBox="0 0 ${W} ${H}" font-family="Arial, Helvetica, sans-serif">`
    + `<rect width="${W}" height="${H}" fill="#fff"/>`
    + `<path d="${mods}" fill="#000"/>`
    + `<rect x="${x.toFixed(2)}" y="${m.toFixed(2)}" width="${larg.toFixed(2)}" height="${faixa.toFixed(2)}" rx="${(0.6 * k).toFixed(2)}" fill="${VERMELHO}"/>`
    + `<text x="${(x + 1.2 * k).toFixed(2)}" y="${(m + faixa * 0.72).toFixed(2)}" font-size="${(2.3 * k).toFixed(2)}" font-weight="700" fill="#fff" letter-spacing="${(0.15 * k).toFixed(2)}">${NOME_TIPO[tipo]}</text>`
    + corpo
    + `<text x="${x.toFixed(2)}" y="${(H - m - fR * 1.5).toFixed(2)}" font-size="${fC.toFixed(2)}" font-weight="700" font-family="'Courier New', monospace" letter-spacing="${(0.25 * k).toFixed(2)}">${esc(codigo)}</text>`
    + `<text x="${x.toFixed(2)}" y="${(H - m).toFixed(2)}" font-size="${fR.toFixed(2)}" fill="#444">MAZA Automação</text>`
    + `</svg>`;
}

function baixar(nome: string, blob: Blob) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = nome; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export function Etiqueta({ codigo, tipo, titulo, sub }: { codigo: string; tipo: TipoEtiqueta; titulo: string; sub: string }) {
  const [tamanho, setTamanho] = useState<Tamanho>(tipo === 'equipamento' ? 'pequena' : 'grande');
  const svg = useMemo(() => svgEtiqueta({ codigo, tipo, titulo, sub, tamanho }), [codigo, tipo, titulo, sub, tamanho]);
  const { w, h, nome } = TAMANHOS[tamanho];
  const arquivo = `etiqueta-${codigo}-${w}x${h}mm`;

  function imprimir() {
    const st = document.createElement('style');
    st.textContent = `@media print { @page { size: ${w}mm ${h}mm; margin: 0; } }`;
    document.head.appendChild(st);
    window.print();
    setTimeout(() => st.remove(), 1000);
  }
  async function png() {
    const dpi = 600, px = (mm: number) => Math.round((mm / 25.4) * dpi);
    const img = new Image();
    img.src = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    await img.decode();
    const cv = document.createElement('canvas'); cv.width = px(w); cv.height = px(h);
    cv.getContext('2d')!.drawImage(img, 0, 0, cv.width, cv.height);
    URL.revokeObjectURL(img.src);
    cv.toBlob((b) => b && baixar(`${arquivo}.png`, b), 'image/png');
  }

  return (
    <div className="etiqueta-area">
      <div className="etiqueta-tamanhos" role="radiogroup" aria-label="Tamanho da etiqueta">
        {(Object.keys(TAMANHOS) as Tamanho[]).map((t) => (
          <button key={t} type="button" role="radio" aria-checked={t === tamanho} className={t === tamanho ? 'ativo' : ''} onClick={() => setTamanho(t)}>{TAMANHOS[t].nome}</button>
        ))}
      </div>
      <div className="etiqueta-svg" id="etiqueta-impressao" style={{ width: `${w}mm`, maxWidth: '100%', aspectRatio: `${w} / ${h}` }} dangerouslySetInnerHTML={{ __html: svg }} />
      <p className="nota">
        {tipo === 'equipamento' ? 'O QR abre a ficha deste equipamento.' : `Ponto de controle: o técnico lê o QR e o app abre o checklist ${tipo === 'parque' ? 'do parque' : 'desta parte'} na OS dele (ou mostra o que falta preencher aqui).`}
        {' '}O código pode ser ditado por telefone. Na rua, prefira {TAMANHOS.grande.nome} com verniz UV, longe de sol direto o dia todo.
      </p>
      <div className="botoes">
        <button className="btn" onClick={imprimir}><Printer size={18} aria-hidden /> Imprimir ({nome})</button>
        <button className="btn-fantasma" onClick={() => baixar(`${arquivo}.svg`, new Blob([svg], { type: 'image/svg+xml' }))}><Download size={16} aria-hidden /> SVG (vetor)</button>
        <button className="btn-fantasma" onClick={png}><Download size={16} aria-hidden /> PNG 600 dpi</button>
      </div>
    </div>
  );
}
